import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import { collection, onSnapshot, orderBy, query } from "firebase/firestore";
import { db } from "../lib/firebase";
import { api } from "../lib/api";
import { useSession } from "../auth/AuthProvider";
import { formatJalali, formatNumber } from "../lib/format";
import { useT } from "../i18n";
import type { StringKey } from "../i18n/fa";

interface Lead {
  id: string;
  name: string;
  phone: string;
  source: string;
  eventDateApprox: string | null;
  guestEstimate: number | null;
  note: string | null;
  stage: string;
}

const STAGES: { value: string; labelKey: StringKey; color: "default" | "info" | "warning" | "success" | "error" }[] = [
  { value: "new", labelKey: "crm_stage_new", color: "default" },
  { value: "contacted", labelKey: "crm_stage_contacted", color: "info" },
  { value: "visited", labelKey: "crm_stage_visited", color: "info" },
  { value: "quoted", labelKey: "crm_stage_quoted", color: "warning" },
  { value: "won", labelKey: "crm_stage_won", color: "success" },
  { value: "lost", labelKey: "crm_stage_lost", color: "error" },
];

const SOURCE_KEY: Record<string, StringKey> = {
  walk_in: "crm_source_walk_in",
  phone: "crm_source_phone",
  instagram: "crm_source_instagram",
  referral: "crm_source_referral",
  other: "crm_source_other",
};

const NEXT_STAGE: Record<string, string> = {
  new: "contacted",
  contacted: "visited",
  visited: "quoted",
  quoted: "won",
};

export function CrmPage() {
  const { orgId } = useSession();
  const [leads, setLeads] = useState<Lead[]>([]);
  const [addOpen, setAddOpen] = useState(false);
  const [form, setForm] = useState({ name: "", phone: "", source: "phone", guests: "", note: "" });
  const [toast, setToast] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const t = useT();

  useEffect(() => {
    if (!orgId) return;
    const q = query(
      collection(db, "organizations", orgId, "leads"),
      orderBy("createdAt", "desc"),
    );
    return onSnapshot(q, (snap) => {
      setLeads(snap.docs.map((d) => ({ id: d.id, ...(d.data() as Omit<Lead, "id">) })));
    });
  }, [orgId]);

  async function addLead() {
    setBusy(true);
    try {
      await api(`/orgs/${orgId}/crm/leads`, {
        method: "POST",
        body: {
          name: form.name,
          phone: form.phone,
          source: form.source,
          guestEstimate: form.guests ? Number(form.guests) : undefined,
          note: form.note || undefined,
        },
      });
      setToast(t("crm_toast_lead_saved"));
      setAddOpen(false);
      setForm({ name: "", phone: "", source: "phone", guests: "", note: "" });
    } catch (e) {
      setToast(e instanceof Error ? e.message : t("common_error"));
    } finally {
      setBusy(false);
    }
  }

  async function setStage(lead: Lead, stage: string) {
    try {
      await api(`/orgs/${orgId}/crm/leads/${lead.id}`, {
        method: "PATCH",
        body: { stage },
      });
    } catch (e) {
      setToast(e instanceof Error ? e.message : t("common_error"));
    }
  }

  return (
    <>
      <Stack direction="row" alignItems="center" mb={2} spacing={2}>
        <Typography variant="h5" sx={{ flex: 1 }}>
          {t("crm_title")}
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={() => setAddOpen(true)}>
          {t("crm_new_lead")}
        </Button>
      </Stack>

      <Box sx={{ display: "flex", gap: 2, overflowX: "auto", pb: 2 }}>
        {STAGES.map((stage) => {
          const items = leads.filter((l) => l.stage === stage.value);
          return (
            <Box key={stage.value} sx={{ minWidth: 230, flex: "0 0 230px" }}>
              <Stack direction="row" alignItems="center" spacing={1} mb={1}>
                <Chip size="small" color={stage.color} label={t(stage.labelKey)} />
                <Typography variant="caption" color="text.secondary">
                  {formatNumber(items.length)}
                </Typography>
              </Stack>
              <Stack spacing={1}>
                {items.map((lead) => (
                  <Card key={lead.id} variant="outlined">
                    <CardContent sx={{ p: 1.5, "&:last-child": { pb: 1.5 } }}>
                      <Typography variant="subtitle2">{lead.name}</Typography>
                      <Typography variant="body2" color="text.secondary" dir="ltr" textAlign="left">
                        {lead.phone}
                      </Typography>
                      <Stack direction="row" spacing={0.5} mt={0.5} flexWrap="wrap">
                        <Chip size="small" variant="outlined" label={SOURCE_KEY[lead.source] ? t(SOURCE_KEY[lead.source]) : lead.source} />
                        {lead.guestEstimate && (
                          <Chip size="small" variant="outlined" label={t("crm_guest_estimate_chip", { count: lead.guestEstimate })} />
                        )}
                      </Stack>
                      {lead.eventDateApprox && (
                        <Typography variant="caption" color="text.secondary">
                          {t("crm_event_approx", { date: formatJalali(lead.eventDateApprox) })}
                        </Typography>
                      )}
                      {lead.note && (
                        <Typography variant="caption" display="block" color="text.secondary">
                          {lead.note}
                        </Typography>
                      )}
                      {!["won", "lost"].includes(lead.stage) && (
                        <Stack direction="row" spacing={1} mt={1}>
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => setStage(lead, NEXT_STAGE[lead.stage])}
                          >
                            {t("crm_advance_next", {
                              label: (() => {
                                const k = STAGES.find((s) => s.value === NEXT_STAGE[lead.stage])?.labelKey;
                                return k ? t(k) : "";
                              })(),
                            })}
                          </Button>
                          <Button size="small" color="error" onClick={() => setStage(lead, "lost")}>
                            {t("crm_stage_lost")}
                          </Button>
                        </Stack>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Box>
          );
        })}
      </Box>

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t("crm_dialog_title")}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <TextField
              label={t("crm_name")}
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              autoFocus
            />
            <TextField
              label={t("crm_phone")}
              placeholder="07XXXXXXXXX"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
            />
            <TextField
              select
              label={t("crm_source_label")}
              value={form.source}
              onChange={(e) => setForm({ ...form, source: e.target.value })}
            >
              {Object.entries(SOURCE_KEY).map(([value, key]) => (
                <MenuItem key={value} value={value}>
                  {t(key)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={t("crm_guests_label")}
              type="number"
              value={form.guests}
              onChange={(e) => setForm({ ...form, guests: e.target.value })}
            />
            <TextField
              label={t("common_note_optional")}
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>{t("common_cancel")}</Button>
          <Button
            variant="contained"
            onClick={addLead}
            disabled={busy || form.name.trim().length < 2 || !/^(\+93|0)\d{9}$/.test(form.phone)}
          >
            {t("common_submit")}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={4000} onClose={() => setToast(null)}>
        <Alert onClose={() => setToast(null)} closeText={t("common_close")} severity="info">
          {toast}
        </Alert>
      </Snackbar>
    </>
  );
}
